import { createSlice, createSelector } from '@reduxjs/toolkit';
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory } from '../common/reducers/utilities';
import { ensureArray } from '../common/utils/arrayUtilities';

const compareJobTag = (s1, s2) => s1.id === s2.id

const jobTagSlice = createSlice({
    name: 'jobTag',
    initialState: {
        data: []
    },
    reducers: {
        set: (state, action) => {
            state.data = action.payload ?? []
        },
        addOrUpdate: (state, action) => {
            for (const tag of ensureArray(action.payload)) {
                let idx = state.data.findIndex(t => compareJobTag(tag, t));
                if (idx >= 0) {
                    state.data.splice(idx, 1, tag);
                } else {
                    state.data.push(tag);
                }
            }
        },
        remove: (state, action) => {
            for (const tag of ensureArray(action.payload)) {
                let idx = state.data.findIndex(t => compareJobTag(t, tag));
                if (idx >= 0) {
                    state.data.splice(idx, 1);
                }
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => jobTagSlice.getInitialState());
    }
});

// #region network operations
const fetchJobTags = network => network.get('/api/v1/jobTags', jobTagSlice.actions.set);
export const createJobTag = (network, tag) => network.post('/api/v1/jobTag', tag, jobTagSlice.actions.addOrUpdate);
export const updateJobTag = (network, tag) => network.put('/api/v1/jobTag', tag, jobTagSlice.actions.addOrUpdate);
export const removeJobTag = (network, tag) => network.delete('/api/v1/jobTag/' + encodeURIComponent(tag.id), jobTagSlice.actions.remove);
// #endregion

// #region permission checks
export const canUpdateJobTag = (credentials) => credentials.access.hasFull('administrator');
export const canCreateJobTag = (credentials) => credentials.access.hasFull('administrator');
export const canRemoveJobTag = (credentials) => credentials.access.hasFull('administrator');
// #endregion

// #region selectors
const selectJobTags = state => state.jobTag.data;

// we now make a reselect to memoize a select function parameterized by jobid and expose that factory through a convenience function
const selectJobTagByName = createSelector([selectJobTags, (jobTag, name) => name], (jobTag, name) => jobTag.find(tag => tag.tag === name));
const selectJobTag = name => state => selectJobTagByName(state, name);
// #endregion

// #region listeners
const listen = {
    jobTagAdded: (jobTag, { dispatch }) => dispatch(jobTagSlice.actions.addOrUpdate(jobTag)),
    jobTagUpdated: (jobTag, { dispatch }) => dispatch(jobTagSlice.actions.addOrUpdate(jobTag)),
    jobTagRemoved: (jobTag, { dispatch }) => dispatch(jobTagSlice.actions.remove(jobTag))
}
// #endregion

// #region custom hooks
export const [useJobTags, realizeJobTags] = dataHookFactory(selectJobTags, { run: fetchJobTags, listen });
export const [useJobTag] = dataHookFactory(selectJobTag, initMapFactory(name => fetchJobTags, listen));
// #endregion


const reducerDetails = { name: jobTagSlice.name, reducer: jobTagSlice.reducer };
export default reducerDetails;